
type Optional<T> = T | null | undefined;

/**Contains functions for sorting arrays */
export module SortBy {
    /**
     * Generates a function for sorting an array of T by the specified string property.
     * Used like this: persons.sort(SortBy.String(person => person.name))
     */
    export function String<T>(prop: (t: T) => Optional<string>): (a: T, b: T) => number{
        return (a: T, b: T) => {
            const sa = prop(a) ?? "";
            const sb = prop(b) ?? "";
            return sa.localeCompare(sb);
        }
    }

    /**
     * Generates a function for sorting an array of T by the specified date property.
     * Used like this: persons.sort(SortBy.String(person => person.birthDate))
     */
    export function Date<T>(prop: (t: T) => Optional<Date>): (a: T, b: T) => number {
        return (a: T, b: T) => {
            const sa = prop(a);
            const sb = prop(b);
            if (!sa) {
                if (!sb)
                    return 0;
                return -1;
            }
            if (!sb)
                return 1;

            return sa.valueOf() - sb.valueOf();
        }
    }

    /**
     * Generates a function for sorting an array of T by the specified number property.
     * Used like this: persons.sort(SortBy.String(person => person.numFingers))
     */
    export function Number<T>(prop: (t: T) => Optional<number>): (a: T, b: T) => number {
        return (a: T, b: T) => {
            const sa = prop(a) ?? 0;
            const sb = prop(b) ?? 0;

            return sa - sb;
        }
    }

    export function StringArray<T>(prop: (t: T) => Optional<Optional<string>[]>): (a: T, b: T) => number {
        return (a: T, b: T) => {
            const sa = prop(a) ?? [];
            const sb = prop(b) ?? [];
            const minLength = Math.min(sa.length, sb.length);
            for (let i = 0; i < minLength; i++) {
                const saa = sa[i] ?? "";
                const sba = sb[i] ?? "";
                const result = saa.localeCompare(sba);
                if (result !== 0)
                    return result;
            }
            return sa.length - sb.length;
        }
    }

    export function Array<T, TA>(prop: (t: T) => Optional<TA[]>, comparer: (a: TA, b: TA) => number): (a: T, b: T) => number {
        return (a: T, b: T) => {
            const sa = prop(a) ?? [];
            const sb = prop(b) ?? [];
            const minLength = Math.min(sa.length, sb.length);
            for (let i = 0; i < minLength; i++) {
                const saa = sa[i];
                const sba = sb[i];
                const result = comparer(saa, sba);
                if (result !== 0)
                    return result;
            }
            return sa.length - sb.length;
        };
    }

}
